import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { getDistance } from "geolib";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { supabase } from "./supabaseClient";
import ListingDetails from "./ListingDetails";
import {
  FiMapPin,
  FiChevronDown,
  FiChevronUp,
  FiSearch,
  FiShare2,
  FiMenu,
  FiX,
  FiArrowRight,
  FiZap,
} from "react-icons/fi";
import MapView from "./Mapview";
import "./App.css";
import MiniDashboard from "./MiniDashboard";
import Chatbot from "./Chatbot";
import Directory from "./Directory";
import EventsPage from "./EventsPage";
import JoinUs from "./JoinUs";
import { ThemeProvider } from "./ThemeContext";
import Footer from "./Footer";
import AuthPrompt from "./AuthPrompt";
import ChatDashboard from "./ChatDashboard";
import { Navigate } from "react-router-dom";
import NotificationCenter from "./NotificationCenter";
import NotificationCenterPage from "./NotificationCenterPage";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfService from "./TermsOfService";
import UpdatePassword from "./update-password";
import ConfirmSignup from "./ConfirmSignup";
import Welcome from "./Welcome";
import SignupError from "./SignupError";
import SocialMediaDropdown from "./SocialMediaDropdown";
import Profile from "./Profile";
import {
  FaHome,
  FaUserGraduate,
  FaQuestionCircle,
  FaUserPlus,
} from "react-icons/fa";
import InfiniteMenu from "./InfiniteMenu";
import Threads from "./Threads";

const items = [
  {
    image:
      "https://raw.githubusercontent.com/github/explore/main/topics/react/react.png",
    link: "https://react.dev/",
    title: "React.js",
    description: "A powerful library for building UIs.",
  },
  {
    image:
      "https://assets.vercel.com/image/upload/v1662130559/front/favicon/vercel/180x180.png",
    link: "https://vercel.com/",
    title: "Vercel",
    description: "Lightning-fast hosting and deployment.",
  },
  {
    image:
      "https://raw.githubusercontent.com/github/explore/main/topics/supabase/supabase.png",
    link: "https://supabase.com/",
    title: "Supabase",
    description: "Open source Firebase alternative.",
  },
  {
    image:
      "https://raw.githubusercontent.com/github/explore/main/topics/typescript/typescript.png",
    link: "https://www.typescriptlang.org/",
    title: "TypeScript",
    description: "Strongly typed JavaScript for large apps.",
  },
  {
    image:
      "https://raw.githubusercontent.com/github/explore/main/topics/nodejs/nodejs.png",
    link: "https://nodejs.org/",
    title: "Node.js",
    description: "JavaScript runtime built on Chrome's V8 engine.",
  },
  {
    image:
      "https://raw.githubusercontent.com/github/explore/main/topics/nextjs/nextjs.png",
    link: "https://nextjs.org/",
    title: "Next.js",
    description: "The React framework for production.",
  },
];

const CHARLOTTE_CENTER = { latitude: 35.2271, longitude: -80.8431 };

// Smooth scroll to section
const smoothScrollTo = (elementId) => {
  const element = document.getElementById(elementId);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

// Landing Page Navbar Component with smooth scroll
function LandingNavbar({ onAuth }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    smoothScrollTo(sectionId);
    setIsMenuOpen(false);
  };

  return (
    <motion.nav
      className={`enhanced-landing-navbar ${scrolled ? "scrolled" : ""}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="landing-nav-container">
        <motion.div className="landing-nav-brand" whileHover={{ scale: 1.05 }}>
          <img
            src="/help.png"
            alt="CLT Student Hub logo"
            className="logo-image"
          />
          <span className="landing-nav-title">CLT StudentHub</span>
        </motion.div>

        <div className={`landing-nav-links ${isMenuOpen ? "active" : ""}`}>
          <motion.a
            href="#home"
            onClick={(e) => handleNavClick(e, "home")}
            whileHover={{ y: -2 }}
          >
            <FaHome className="nav-icon" /> Home
          </motion.a>
          <motion.a
            href="#students"
            onClick={(e) => handleNavClick(e, "students")}
            whileHover={{ y: -2 }}
          >
            <FaUserGraduate className="nav-icon" /> Students
          </motion.a>
          <motion.a
            href="#why-us"
            onClick={(e) => handleNavClick(e, "why-us")}
            whileHover={{ y: -2 }}
          >
            <FaQuestionCircle className="nav-icon" /> Why
          </motion.a>
          <Link to="/join-us" className="landing-nav-link">
            <FaUserPlus className="nav-icon" /> Join
          </Link>
          <motion.button
            onClick={() => onAuth("signup")}
            className="landing-nav-cta"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Started
          </motion.button>
        </div>

        <button
          className={`landing-nav-toggle ${isMenuOpen ? "open" : ""}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <FiX size={22} /> : <FiMenu size={22} />}
        </button>
      </div>
    </motion.nav>
  );
}

function LandingPage({ onAuth }) {
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

  return (
    <div className="enhanced-landing-page">
      <LandingNavbar onAuth={onAuth} />

      {/* Scroll Progress Bar */}
      <motion.div
        className="scroll-progress"
        style={{ scaleX: scrollYProgress }}
      />

      {/* Hero Section */}
      <section className="enhanced-hero-section" id="home">
        <motion.div className="hero-content" style={{ opacity }}>
          <motion.div
            className="hero-badge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <FiZap /> Shivang Patel
          </motion.div>

          <motion.h1
            className="enhanced-hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            CLT StudentHub
          </motion.h1>

          <motion.p
            className="enhanced-hero-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Find events, clubs and local student resources across Charlotte +
            all in one place.
          </motion.p>

          <motion.div
            className="enhanced-hero-buttons"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <motion.button
              className="enhanced-primary-btn"
              onClick={() => onAuth("signup")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Started <FiArrowRight />
            </motion.button>
            <Link to="/join-us">
              <motion.button
                className="enhanced-secondary-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Join Us
              </motion.button>
            </Link>
          </motion.div>

          <motion.div
            className="enhanced-hero-image"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, duration: 1 }}
          >
            <div className="floating-card">
              <img
                src="/help.png"
                alt="Students collaborating"
                onError={(e) => (e.target.style.display = "none")}
              />
              <div className="floating-overlay">
                Charlotte • Community • Clubs
              </div>
            </div>
          </motion.div>
        </motion.div>
        <div className="threads-bg">
          <Threads amplitude={0.7} distance={0} enableMouseInteraction />
        </div>
      </section>

      {/* Features Section */}
      <motion.div
        className="section-header"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h3>Why Choose CLT Student Hub?</h3>
      </motion.div>

      {/* Testimonials Section */}

      {/* CTA Section */}
      <div style={{ height: "600px", position: "relative" }}>
        <InfiniteMenu items={items} />
      </div>

      {/* Footer */}
      <footer className="enhanced-landing-footer">
        <div className="footer-content"></div>
        <div className="footer-bottom">
          <p>&copy; 2026 CLT StudentHub. All rights reserved.</p>
          <div className="footer-legal">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <a href="mailto:shivangpatel2050@gmail.com">Contact Us</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Navbar Component (unchanged backend)
function Navbar({ user, handleLogout }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSocialOpen, setIsSocialOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 768) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsSettingsOpen(false);
    setIsSocialOpen(false);
    document.body.style.overflow = !isMenuOpen ? "hidden" : "auto";
  };

  const closeAll = () => {
    setIsMenuOpen(false);
    setIsSettingsOpen(false);
    setIsSocialOpen(false);
    document.body.style.overflow = "auto";
  };

  useEffect(() => {
    const handleClickOutside = () => {
      if (isMenuOpen || isSettingsOpen || isSocialOpen) {
        closeAll();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isMenuOpen, isSettingsOpen, isSocialOpen]);

  return (
    <>
      <nav className="navbar">
        <div className="navbar-brand">
          <Link
            to="/"
            className="logo"
            onClick={closeAll}
            aria-label="CLT Student Hub"
          >
            <img
              src="/help.png"
              alt="CLT Student Hub logo"
              className="logo-image"
            />
          </Link>
        </div>

        <div className="nav-controls">
          {user && (
            <div className="user-info">
              <Link to="/social" className="nav-social-link" onClick={closeAll}>
                <FiShare2 style={{ marginRight: "8px" }} />
              </Link>
              <button onClick={handleLogout} className="logout-btn">
                Logout
              </button>
            </div>
          )}

          {windowWidth <= 768 && (
            <button
              className={`hamburger ${isMenuOpen ? "open" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleMenu();
              }}
              aria-label="Menu"
              aria-expanded={isMenuOpen}
            >
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </button>
          )}

          {windowWidth > 768 && (
            <div className="desktop-nav">
              <Link to="/" onClick={closeAll}>
                Home
              </Link>
              <Link to="/join-us" onClick={closeAll}>
                {" "}
                Join Us{" "}
              </Link>
              {user && (
                <Link to="/community-chat" onClick={closeAll}>
                  Community Chat
                </Link>
              )}
            </div>
          )}
        </div>

        {windowWidth <= 768 && (
          <div className={`mobile-menu ${isMenuOpen ? "open" : ""}`}>
            <div className="mobile-nav-links">
              <Link to="/" onClick={closeAll}>
                Home
              </Link>
              {user && (
                <Link to="/community-chat" onClick={closeAll}>
                  Community Chat
                </Link>
              )}
            </div>
          </div>
        )}

        {(isMenuOpen || isSettingsOpen || isSocialOpen) && (
          <div className="nav-overlay" onClick={closeAll} />
        )}
      </nav>
    </>
  );
}

function Home({ user }) {
  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    category: "all",
    radius: 0,
    sort: "newest",
  });
  const [isMapCollapsed, setIsMapCollapsed] = useState(false);

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const { data, error } = await supabase.from("listings").select("*");
        if (error) throw error;
        setListings(data);
        setFilteredListings(data);
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchListings();
  }, []);

  useEffect(() => {
    let results = [...listings];

    if (filters.search) {
      results = results.filter(
        (item) =>
          item.title.toLowerCase().includes(filters.search.toLowerCase()) ||
          item.description.toLowerCase().includes(filters.search.toLowerCase()),
      );
    }

    if (filters.category !== "all") {
      results = results.filter((item) => item.category === filters.category);
    }

    if (filters.radius > 0) {
      results = results.filter((item) => {
        if (!item.latitude || !item.longitude) return false;
        const distance =
          getDistance(
            { latitude: item.latitude, longitude: item.longitude },
            CHARLOTTE_CENTER,
          ) / 1000;
        return distance <= filters.radius;
      });
    }

    results.sort((a, b) => {
      if (filters.sort === "newest")
        return new Date(b.created_at) - new Date(a.created_at);
      if (filters.sort === "alpha") return a.title.localeCompare(b.title);
      return 0;
    });

    setFilteredListings(results);
  }, [filters, listings]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const toggleMap = () => {
    setIsMapCollapsed(!isMapCollapsed);
  };

  return (
    <div className="home-container">
      <section className="hero-section">
        <h1>
          <span>Welcome, {user}</span>
        </h1>
        <p>Find clubs, events, and resources tailored to your interests</p>
        <p>
          <i>Create an account for access to all features!</i>
        </p>
      </section>

      <div className="search-filters">
        <div className="search-bar">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search clubs, events..."
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
          />
        </div>

        <div className="filter-group">
          <select
            value={filters.radius}
            onChange={(e) =>
              handleFilterChange("radius", Number(e.target.value))
            }
          >
            <option value={0}>Any Distance</option>
            <option value={5}>Within 5km</option>
            <option value={10}>Within 10km</option>
            <option value={25}>Within 25km</option>
          </select>

          <select
            value={filters.sort}
            onChange={(e) => handleFilterChange("sort", e.target.value)}
          >
            <option value="newest">Newest First</option>
            <option value="alpha">A-Z</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading opportunities...</p>
        </div>
      ) : filteredListings.length === 0 ? (
        <div className="no-results">
          <p>No matching results found.</p>
        </div>
      ) : (
        <>
          <div className="map-toggle" onClick={toggleMap}>
            {isMapCollapsed ? (
              <>
                <FiChevronDown /> Show Map
              </>
            ) : (
              <>
                <FiChevronUp /> Hide Map
              </>
            )}
          </div>
          {!isMapCollapsed && <MapView listings={filteredListings} />}
          <div className="listings-grid">
            {filteredListings.map((listing) => (
              <div key={listing.id} className="listing-card">
                <div className="card-header">
                  <h3>{listing.title}</h3>
                  <span className="category-badge">{listing.category}</span>
                </div>
                <p className="description">{listing.description}</p>
                <div className="card-footer">
                  <span className="location">
                    <FiMapPin /> {listing.location || "Charlotte, NC"}
                  </span>
                  <Link to={`/listing/${listing.id}`} className="details-link">
                    View Details →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setUser(data.session.user.email);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session ? session.user.email : null);
      },
    );

    return () => listener.subscription.unsubscribe();
  }, []);

  const handleAuth = (mode) => {
    setAuthMode(mode);
  };

  const handleAuthSuccess = (userEmail) => {
    setUser(userEmail);
    setAuthMode(null);
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setAuthMode(null);
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <ThemeProvider>
      <Router>
        <div className="app-wrapper">
          {(user || authMode) && (
            <Navbar user={user} handleLogout={handleLogout}>
              {user && <NotificationCenter userId={user.id} />}
            </Navbar>
          )}

          <main className="main-content">
            <Routes>
              <Route
                path="/"
                element={
                  user ? (
                    <Home user={user} />
                  ) : authMode ? (
                    <AuthPrompt
                      mode={authMode}
                      onAuth={handleAuthSuccess}
                      onCancel={() => setAuthMode(null)}
                    />
                  ) : (
                    <LandingPage onAuth={handleAuth} />
                  )
                }
              />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/directory" element={<Directory />} />
              <Route path="/join-us" element={<JoinUs />} />
=              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/confirm-signup" element={<ConfirmSignup />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/signup-error" element={<SignupError />} />
              <Route path="/social" element={<SocialMediaDropdown />} />
              <Route path="/update-password" element={<UpdatePassword />} />
              <Route path="/listing/:id" element={<ListingDetails />} />


              {user && (
                <>
                  <Route path="/profile" element={<Profile />} />
                  <Route
                    path="/community-chat"
                    element={<ChatDashboard user={user} />}
                  />
                  <Route
                    path="/notifications"
                    element={<NotificationCenterPage user={user} />}
                  />
                </>
              )}

              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </main>

          {user && <MiniDashboard />}
          {user && <Footer />}

         
        </div>

        <Chatbot />
      </Router>
    </ThemeProvider>
  );
}

export default App;
